import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Clock, Plus, Trash2, Info, X } from 'lucide-react';
import api from '../utils/api';

interface PendingConfirm {
  // Human-readable label of what's being booked, e.g. "Maths — A-Level (Online)"
  summary: string;
  // When the lesson is, formatted for humans
  whenLabel: string;
  // Number of sessions being booked (>1 for repeat lessons)
  sessionsCount: number;
  // Total minutes per session
  durationMin: number;
  // Per-session price (£)
  pricePerSession: number;
  // Total price across all sessions (£)
  totalPrice: number;
  // Whether this booking is a consultation (flat £5) — affects copy
  isConsultation: boolean;
  // Whether this booking will be paid from an existing hour package (no Stripe link)
  willUsePackage: boolean;
  // Actually submit the booking — invoked when user confirms in the modal
  onConfirm: () => Promise<void>;
}

// Extend window interface for resize tracking
declare global {
  interface Window {
    __hasResized?: boolean;
  }
}

// Hook to detect mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

interface Subject {
  id: string;
  name: string;
  category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  onlineRate: number;
  inPersonRate: number;
}

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  tutor: {
    id: string;
    name: string;
  };
}

const BookingPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  
  // Track window resizes to differentiate between initial mobile load and resize.
  // The mobile re-fetch effect below uses `__hasResized` to skip its 100ms warm-up
  // delay once the layout has actually settled.
  React.useEffect(() => {
    const handleResize = () => {
      if (!window.__hasResized) {
        window.__hasResized = true;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [bookingType, setBookingType] = useState<'LESSON' | 'CONSULTATION'>('LESSON');
  const [isOnline, setIsOnline] = useState(false);
  const [isRepeatLesson, setIsRepeatLesson] = useState(false);
  const [repeatWeeks, setRepeatWeeks] = useState(4);

  // New flexible booking state
  const [newBookingData, setNewBookingData] = useState({
    selectedSubjects: [] as { subjectId: string; duration: number }[],
    selectedDate: '',
    selectedStartTime: '',
    totalDuration: 60, // in minutes
    isOnline: true,
    calculatedPrice: 0
  });
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [showNewInterface, setShowNewInterface] = useState(false);

  // Pre-booking price confirmation modal — opens when the student clicks
  // a slot, so they see exactly what they'll be charged before submitting.
  // Closing without confirming leaves the booking unsent.
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [submittingConfirm, setSubmittingConfirm] = useState(false);

  const fetchTimeSlots = useCallback(async () => {
    setLoading(true);

    const apiUrl = `/availability/subject/${selectedSubject}?date=${selectedDate}`;

    try {
      if (isMobile && !window.__hasResized) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const response = await Promise.race([
        api.get(apiUrl),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout after 8 seconds')), 8000)
        )
      ]) as any;

      setTimeSlots(response.data);
    } catch (error: any) {
      if (error.message?.includes('timeout') || error.message?.includes('network')) {
        try {
          const retryResponse = await api.get(apiUrl);
          setTimeSlots(retryResponse.data);
          return;
        } catch {
          // retry failed; fall through to finally
        }
      }
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedSubject, isMobile, setLoading, setTimeSlots]);

  // Mobile: pre-selected date/subject sometimes don't trigger the main effect
  // on first paint; nudge a re-fetch once after mount.
  React.useEffect(() => {
    if (isMobile && selectedDate && selectedSubject && timeSlots.length === 0) {
      const timer = setTimeout(() => {
        fetchTimeSlots();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isMobile, selectedDate, selectedSubject, timeSlots.length, fetchTimeSlots]);

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    // Handle preselected subjects from URL parameters
    const subjectsParam = searchParams.get('subjects');
    if (subjectsParam && subjects.length > 0) {
      const subjectIds = subjectsParam.split(',').filter(Boolean);
      // For booking page, select the first subject if multiple are provided
      if (subjectIds.length > 0) {
        // Validate that the subject exists
        const firstSubject = subjects.find(s => s.id === subjectIds[0]);
        if (firstSubject) {
          setSelectedSubject(subjectIds[0]);
        }
      }
    }
  }, [searchParams, subjects]);

  useEffect(() => {
    if (selectedSubject && selectedDate) {
      fetchTimeSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject, selectedDate]);

  // Force layout fix for mobile rendering issues
  useEffect(() => {
    if (timeSlots.length > 0) {
      if (isMobile) {
        const timer = setTimeout(() => {
          // Force container visibility
          const container = document.querySelector('[data-booking-slots]') as HTMLElement;
          if (container) {
            container.style.opacity = '0';
            container.style.visibility = 'hidden';
            void container.offsetHeight; // Trigger reflow
            container.style.opacity = '1';
            container.style.visibility = 'visible';
          }
          
          // Force parent layout recalculation
          const mainContent = document.querySelector('main') as HTMLElement;
          if (mainContent) {
            mainContent.style.transform = 'translateZ(0)';
            void mainContent.offsetHeight;
            mainContent.style.transform = '';
          }
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }
  }, [timeSlots, isMobile]);

  const fetchSubjects = async () => {
    try {
      // Students should only see their selected subjects with pricing info
      if (user?.role === 'STUDENT') {
        const response = await api.get('/subjects/student/my-subjects');
        setSubjects(response.data);

        // If no subjects assigned, show all subjects for testing
        if (!response.data || response.data.length === 0) {
          const allSubjectsResponse = await api.get('/subjects');
          setSubjects(allSubjectsResponse.data);
        }
      } else {
        // Tutors/admins can see all subjects
        const response = await api.get('/subjects');
        setSubjects(response.data);
      }
    } catch (error) {
      console.error('BookingPage - Failed to fetch subjects:', error);
    }
  };

  // Duration of a time slot in minutes
  const slotDurationMin = (slot: TimeSlot): number =>
    Math.max(0, Math.round(
      (new Date(slot.endTime).getTime() - new Date(slot.startTime).getTime()) / 60000
    ));

  // Per-session price for a given slot under the current form options.
  // Consultations are a flat £5; lessons use the chosen subject's rate.
  // Returns 0 if the rate isn't known yet (subject still loading).
  const slotPricePerSession = (slot: TimeSlot): number => {
    if (bookingType === 'CONSULTATION') return 5;
    const subject = subjects.find(s => s.id === selectedSubject);
    if (!subject) return 0;
    const rate = isOnline ? subject.onlineRate : subject.inPersonRate;
    if (typeof rate !== 'number' || !isFinite(rate)) return 0;
    return rate * (slotDurationMin(slot) / 60);
  };

  const formatPrice = (amount: number): string =>
    amount > 0 ? `£${amount.toFixed(2)}` : '—';

  // Build a "we're about to book this, here's what it costs" payload and
  // open the confirm modal. Submission only happens if the student clicks
  // Confirm in the modal — there is no charge taken at this stage.
  const handleBooking = (timeSlot: TimeSlot) => {
    const isConsultation = bookingType === 'CONSULTATION';
    const startTime = new Date(timeSlot.startTime);
    // Consultations are a flat 20-minute £5 session regardless of the slot's
    // displayed length, matching the backend's /bookings/consultation contract
    // and the dedicated /consultation page. The slot shown might be 60 min but
    // we only book the first 20.
    const endTime = isConsultation
      ? new Date(startTime.getTime() + 20 * 60 * 1000)
      : new Date(timeSlot.endTime);
    const durationMin = isConsultation ? 20 : slotDurationMin(timeSlot);
    const pricePerSession = isConsultation ? 5 : slotPricePerSession(timeSlot);
    const sessionsCount = !isConsultation && isRepeatLesson ? repeatWeeks : 1;
    const totalPrice = pricePerSession * sessionsCount;

    const subject = subjects.find(s => s.id === selectedSubject);
    const subjectLabel = subject ? formatSubjectWithLevel(subject) : 'Lesson';
    const modeLabel = isConsultation
      ? '20-min consultation'
      : `${isOnline ? 'Online' : 'In-person'} lesson`;
    const summary = `${subjectLabel} · ${modeLabel}`;

    const baseWhen = `${formatDate(startTime.toISOString())}, ${formatTime(startTime.toISOString())}–${formatTime(endTime.toISOString())}`;
    const whenLabel = sessionsCount > 1
      ? `${baseWhen} (and ${sessionsCount - 1} more weekly sessions at the same time)`
      : baseWhen;

    const submit = async () => {
      try {
        if (isConsultation) {
          await api.post('/bookings/consultation', {
            tutorId: timeSlot.tutor.id,
            subjectId: selectedSubject,
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
          });
          window.alert("Consultation request submitted. You haven't been charged — Ethan will send a payment link once the booking is confirmed.");
        } else if (sessionsCount > 1) {
          const bookingPromises = [];
          for (let week = 0; week < sessionsCount; week++) {
            const weekStartTime = new Date(startTime);
            const weekEndTime = new Date(endTime);
            weekStartTime.setDate(startTime.getDate() + (week * 7));
            weekEndTime.setDate(endTime.getDate() + (week * 7));

            bookingPromises.push(api.post('/bookings/request', {
              tutorId: timeSlot.tutor.id,
              subjectId: selectedSubject,
              startTime: weekStartTime.toISOString(),
              endTime: weekEndTime.toISOString(),
              isOnline: isOnline,
            }));
          }
          await Promise.all(bookingPromises);
          window.alert(`${sessionsCount} repeat lesson requests submitted. You haven't been charged — Ethan will send a payment link once each booking is confirmed.`);
        } else {
          await api.post('/bookings/request', {
            tutorId: timeSlot.tutor.id,
            subjectId: selectedSubject,
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            isOnline: isOnline,
          });
          window.alert("Booking request submitted. You haven't been charged — Ethan will send a payment link once the booking is confirmed.");
        }
        fetchTimeSlots();
      } catch (error: any) {
        window.alert(error.response?.data?.message || 'Failed to submit booking request');
        throw error;
      }
    };

    setPendingConfirm({
      summary,
      whenLabel,
      sessionsCount,
      durationMin,
      pricePerSession,
      totalPrice,
      isConsultation: bookingType === 'CONSULTATION',
      willUsePackage: false,
      onConfirm: submit,
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-GB', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'Europe/London'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Europe/London'
    });
  };

  const formatSubjectCategory = (category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY') => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      case 'UNIVERSITY':
        return 'University';
      default:
        return category;
    }
  };

  const formatSubjectWithLevel = (subject: Subject) => {
    const baseFormat = `${subject.name} - ${formatSubjectCategory(subject.category)}`;
    
    // For students, show pricing information for both online and in-person
    if (user?.role === 'STUDENT' && subject.onlineRate && subject.inPersonRate) {
      return `${baseFormat} (Online: £${subject.onlineRate}/hr, In-person: £${subject.inPersonRate}/hr)`;
    }
    
    return baseFormat;
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 6); // Allow booking up to 6 months in advance  
    return maxDate.toISOString().split('T')[0];
  };

  const formatDurationLabel = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (m === 0) return h === 1 ? '1 hour' : `${h} hours`;
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  // New flexible booking functions
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 20; hour++) { // 8 AM to 8 PM
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  const calculateNewPrice = () => {
    if (!newBookingData.selectedSubjects.length) {
      setNewBookingData(prev => ({ ...prev, calculatedPrice: 0 }));
      return;
    }

    let totalPrice = 0;
    newBookingData.selectedSubjects.forEach(subjectDuration => {
      const subject = subjects.find(s => s.id === subjectDuration.subjectId);
      if (subject) {
        const rate = newBookingData.isOnline ? subject.onlineRate : subject.inPersonRate;
        if (rate && typeof rate === 'number') {
          const hours = subjectDuration.duration / 60;
          totalPrice += rate * hours;
        }
      }
    });
    setNewBookingData(prev => ({ ...prev, calculatedPrice: totalPrice }));
  };

  const addSubjectToNewBooking = () => {
    if (!newBookingData.selectedSubjects.length) {
      // First subject gets the full duration
      setNewBookingData(prev => ({
        ...prev,
        selectedSubjects: [{ subjectId: '', duration: prev.totalDuration }]
      }));
    } else {
      // Additional subjects
      setNewBookingData(prev => ({
        ...prev,
        selectedSubjects: [...prev.selectedSubjects, { subjectId: '', duration: 15 }]
      }));
    }
  };

  const updateNewSubjectDuration = (index: number, subjectId: string, duration: number) => {
    const newSubjects = [...newBookingData.selectedSubjects];
    newSubjects[index] = { subjectId, duration };
    setNewBookingData(prev => ({
      ...prev,
      selectedSubjects: newSubjects
    }));
  };

  const removeSubjectFromNewBooking = (index: number) => {
    const newSubjects = newBookingData.selectedSubjects.filter((_, i) => i !== index);
    setNewBookingData(prev => ({
      ...prev,
      selectedSubjects: newSubjects
    }));
  };

  const submitNewBooking = () => {
    if (!newBookingData.selectedDate || !newBookingData.selectedStartTime || !newBookingData.selectedSubjects.length) {
      window.alert('Please fill in all required fields');
      return;
    }

    const startDateTime = new Date(`${newBookingData.selectedDate}T${newBookingData.selectedStartTime}`);
    const endDateTime = new Date(startDateTime.getTime() + newBookingData.totalDuration * 60000);

    const mainSubject = newBookingData.selectedSubjects[0];
    const subject = subjects.find(s => s.id === mainSubject.subjectId);
    const subjectLabel = subject ? formatSubjectWithLevel(subject) : 'Lesson';
    const modeLabel = `${newBookingData.isOnline ? 'Online' : 'In-person'} lesson`;
    const whenLabel = `${formatDate(startDateTime.toISOString())}, ${formatTime(startDateTime.toISOString())}–${formatTime(endDateTime.toISOString())}`;

    const submit = async () => {
      try {
        await api.post('/bookings/request', {
          studentId: user?.id,
          tutorId: timeSlots[0]?.tutor?.id,
          subjectId: mainSubject.subjectId,
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          type: 'LESSON',
          isOnline: newBookingData.isOnline,
        });

        setNewBookingData({
          selectedSubjects: [],
          selectedDate: '',
          selectedStartTime: '',
          totalDuration: 60,
          isOnline: true,
          calculatedPrice: 0,
        });

        window.alert("Booking request submitted. You haven't been charged — Ethan will send a payment link once the booking is confirmed.");
      } catch (error: any) {
        window.alert(error.response?.data?.message || 'Failed to submit booking request');
        throw error;
      }
    };

    setPendingConfirm({
      summary: `${subjectLabel} · ${modeLabel}`,
      whenLabel,
      sessionsCount: 1,
      durationMin: newBookingData.totalDuration,
      pricePerSession: newBookingData.calculatedPrice,
      totalPrice: newBookingData.calculatedPrice,
      isConsultation: false,
      willUsePackage: false,
      onConfirm: submit,
    });
  };

  // Calculate price when subjects or online/offline changes
  React.useEffect(() => {
    calculateNewPrice();
  }, [newBookingData.selectedSubjects, newBookingData.isOnline]);

  // Generate available time slots
  React.useEffect(() => {
    setAvailableTimeSlots(generateTimeSlots());
  }, []);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {user?.role === 'STUDENT' ? 'Book a Lesson' : 'Manage Schedule'}
        </h1>
        <p className="text-gray-600 mt-2">
          {user?.role === 'STUDENT' 
            ? 'Select a subject, date, and time to book your session.'
            : 'View and manage your availability and bookings.'
          }
        </p>
        
        {/* Show preselected subjects notification */}
        {(() => {
          const subjectsParam = searchParams.get('subjects');
          if (subjectsParam && subjects.length > 0) {
            const subjectIds = subjectsParam.split(',').filter(Boolean);
            const selectedSubjectNames = subjectIds
              .map(id => subjects.find(s => s.id === id)?.name)
              .filter(Boolean);
            
            if (selectedSubjectNames.length > 0) {
              return (
                <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                  <p className="text-primary-700 text-sm">
                    <strong>Subject{selectedSubjectNames.length > 1 ? 's' : ''} from previous selection:</strong> {selectedSubjectNames.join(', ')}
                    {selectedSubjectNames.length > 1 && ' (first subject pre-selected)'}
                  </p>
                </div>
              );
            }
          }
          return null;
        })()}
      </div>

      {user?.role === 'STUDENT' && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">You won't be charged when you book.</p>
            <p className="mt-1 text-blue-800">
              Submitting a booking is only a request. Once Ethan confirms it, you'll receive a payment link by email and you can also pay from the Payments page. You'll see the exact price before you confirm each booking.
            </p>
          </div>
        </div>
      )}

      {user?.role === 'STUDENT' && (
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h2 className="card-title">Book Your Session</h2>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showNewInterface}
                    onChange={(e) => setShowNewInterface(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Use new flexible booking (15-min intervals)
                  </span>
                </label>
              </div>
            </div>
          </div>
          
          {/* New Flexible Booking Interface */}
          {showNewInterface ? (
            <div className="space-y-6">
              {/* Date and Time Selection */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={newBookingData.selectedDate}
                    onChange={(e) => setNewBookingData(prev => ({ ...prev, selectedDate: e.target.value }))}
                    className="input"
                    min={getMinDate()}
                    max={getMaxDate()}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lesson Mode *
                  </label>
                  <select
                    value={newBookingData.isOnline ? 'online' : 'in-person'}
                    onChange={(e) => setNewBookingData(prev => ({ ...prev, isOnline: e.target.value === 'online' }))}
                    className="input"
                  >
                    <option value="online">Online</option>
                    <option value="in-person">In-Person</option>
                  </select>
                </div>
              </div>

              {/* Start Time Selection - 15 minute intervals */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time *
                  </label>
                  <select
                    value={newBookingData.selectedStartTime}
                    onChange={(e) => setNewBookingData(prev => ({ ...prev, selectedStartTime: e.target.value }))}
                    className="input"
                    required
                  >
                    <option value="">Choose start time...</option>
                    {availableTimeSlots.map(timeSlot => (
                      <option key={timeSlot} value={timeSlot}>
                        {new Date(`2000-01-01T${timeSlot}`).toLocaleTimeString('en-GB', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true
                        })}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Duration *
                  </label>
                  <select
                    value={newBookingData.totalDuration}
                    onChange={(e) => {
                      const newDuration = parseInt(e.target.value);
                      setNewBookingData(prev => ({ 
                        ...prev, 
                        totalDuration: newDuration,
                        // Reset subjects when duration changes
                        selectedSubjects: prev.selectedSubjects.length > 0 ? [{ subjectId: prev.selectedSubjects[0].subjectId, duration: newDuration }] : []
                      }));
                    }}
                    className="input"
                  >
                    {/* Duration options in 15-minute steps */}
                    {[15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180].map(duration => (
                      <option key={duration} value={duration}>
                        {formatDurationLabel(duration)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Subject Selection */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Subjects to Cover *
                  </label>
                  <button
                    type="button"
                    onClick={addSubjectToNewBooking}
                    className="btn btn-outline btn-sm flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Subject</span>
                  </button>
                </div>

                <div className="space-y-3">
                  {newBookingData.selectedSubjects.map((subjectDuration, index) => (
                    <div key={index} className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end p-4 bg-gray-50 rounded-lg">
                      <div className="col-span-2 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subject
                        </label>
                        <select
                          value={subjectDuration.subjectId}
                          onChange={(e) => updateNewSubjectDuration(index, e.target.value, subjectDuration.duration)}
                          className="input"
                          required
                        >
                          <option value="">Choose subject...</option>
                          {subjects.map((subject) => (
                            <option key={subject.id} value={subject.id}>
                              {formatSubjectWithLevel(subject)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duration (min)
                        </label>
                        <select
                          value={subjectDuration.duration}
                          onChange={(e) => updateNewSubjectDuration(index, subjectDuration.subjectId, parseInt(e.target.value))}
                          className="input"
                        >
                          {[15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180].filter(duration => duration <= newBookingData.totalDuration).map(duration => (
                            <option key={duration} value={duration}>
                              {formatDurationLabel(duration)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <button
                          type="button"
                          onClick={() => removeSubjectFromNewBooking(index)}
                          className="btn btn-outline btn-sm text-red-600 hover:bg-red-50"
                          disabled={newBookingData.selectedSubjects.length === 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {newBookingData.selectedSubjects.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      Click "Add Subject" to specify what subjects will be covered in this lesson
                    </div>
                  )}
                </div>
              </div>

              {/* Price Display */}
              {newBookingData.calculatedPrice > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-800">Total Price:</span>
                    <span className="text-2xl font-bold text-green-900">£{newBookingData.calculatedPrice.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    Based on {newBookingData.isOnline ? 'online' : 'in-person'} rates from subject management
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setNewBookingData({
                      selectedSubjects: [],
                      selectedDate: '',
                      selectedStartTime: '',
                      totalDuration: 60,
                      isOnline: true,
                      calculatedPrice: 0
                    });
                  }}
                  className="btn btn-outline"
                >
                  Reset Form
                </button>
                <button
                  onClick={submitNewBooking}
                  disabled={!newBookingData.selectedDate || !newBookingData.selectedStartTime || !newBookingData.selectedSubjects.length || newBookingData.calculatedPrice === 0}
                  className="btn btn-primary"
                >
                  Request Booking
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <select
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    className="input"
                  >
                    <option value="">Select a subject</option>
                    {Array.isArray(subjects) ? subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {formatSubjectWithLevel(subject)}
                      </option>
                    )) : null}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Type
                  </label>
                  <select
                    value={bookingType}
                    onChange={(e) => setBookingType(e.target.value as 'LESSON' | 'CONSULTATION')}
                    className="input"
                  >
                    <option value="LESSON">Regular Lesson</option>
                    <option value="CONSULTATION">Consultation</option>
                  </select>
                </div>

                {bookingType === 'LESSON' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lesson Mode
                    </label>
                    <select
                      value={isOnline ? 'online' : 'in-person'}
                      onChange={(e) => setIsOnline(e.target.value === 'online')}
                      className="input"
                    >
                      <option value="online">Online</option>
                      <option value="in-person">In-Person</option>
                    </select>
                  </div>
                )}

                {bookingType === 'LESSON' && (
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isRepeatLesson}
                        onChange={(e) => setIsRepeatLesson(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Book as repeat lesson (same time every week)
                      </span>
                    </label>
                    
                    {isRepeatLesson && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Number of weeks
                        </label>
                        <select
                          value={repeatWeeks}
                          onChange={(e) => setRepeatWeeks(parseInt(e.target.value))}
                          className="input w-full"
                        >
                          {[4, 6, 8, 10, 12].map(weeks => (
                            <option key={weeks} value={weeks}>
                              {weeks} weeks
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          This will create {repeatWeeks} booking requests for consecutive weeks at the same time.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={getMinDate()}
                    max={getMaxDate()}
                    className="input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedSubject && selectedDate && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Available Time Slots</h2>
            <p className="card-subtitle">
              {formatDate(selectedDate)} - {Array.isArray(subjects) ? subjects.find(s => s.id === selectedSubject)?.name : 'Unknown Subject'}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading available slots...</p>
            </div>
          ) : !Array.isArray(timeSlots) || timeSlots.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No available time slots for this date.</p>
              <p className="text-sm text-gray-500 mt-1">Try selecting a different date or subject.</p>
            </div>
          ) : (
            <div 
              data-booking-slots
              className="w-full"
              style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                minHeight: '100px'
              }}
            >
              {Array.isArray(timeSlots) ? timeSlots.map((slot) => {
                const perSession = slotPricePerSession(slot);
                const sessions = bookingType === 'LESSON' && isRepeatLesson ? repeatWeeks : 1;
                const totalEstimate = perSession * sessions;
                return (
                <div
                  key={slot.id}
                  className="border border-gray-200 rounded-lg p-4"
                  style={{
                    display: 'block',
                    visibility: 'visible',
                    opacity: 1,
                    minHeight: '80px',
                    width: '100%'
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="font-medium">
                        {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                      </span>
                    </div>
                    {perSession > 0 && (
                      <span className="text-sm font-semibold text-gray-900">
                        {formatPrice(totalEstimate)}
                        {sessions > 1 && (
                          <span className="ml-1 font-normal text-gray-500">
                            ({sessions} × {formatPrice(perSession)})
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    with {slot.tutor.name}
                  </p>
                  {user?.role === 'STUDENT' && (
                    <button
                      onClick={() => handleBooking(slot)}
                      className="w-full btn btn-primary"
                    >
                      Review and book{perSession > 0 ? ` — ${formatPrice(totalEstimate)}` : ''}
                    </button>
                  )}
                </div>
                );
              }) : null}
            </div>
          )}
        </div>
      )}

      {pendingConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start p-6 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Confirm your booking</h2>
                <p className="text-sm text-gray-600 mt-1">Check the price before submitting.</p>
              </div>
              <button
                type="button"
                onClick={() => setPendingConfirm(null)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">What</p>
                <p className="text-base text-gray-900">{pendingConfirm.summary}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">When</p>
                <p className="text-base text-gray-900">{pendingConfirm.whenLabel}</p>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                {pendingConfirm.sessionsCount > 1 ? (
                  <div className="flex items-baseline justify-between text-sm text-gray-700">
                    <span>{pendingConfirm.sessionsCount} sessions × {formatPrice(pendingConfirm.pricePerSession)}</span>
                  </div>
                ) : null}
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-sm font-medium text-gray-700">
                    {pendingConfirm.sessionsCount > 1 ? 'Total if all confirmed' : 'Price'}
                  </span>
                  <span className="text-2xl font-bold text-gray-900">
                    {formatPrice(pendingConfirm.totalPrice)}
                  </span>
                </div>
                {pendingConfirm.isConsultation && (
                  <p className="text-xs text-gray-500 mt-1">
                    Consultations are a flat £5, regardless of length.
                  </p>
                )}
                {!pendingConfirm.isConsultation && pendingConfirm.pricePerSession > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Based on the subject's hourly rate for the selected duration.
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 text-amber-900">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold">You will not be charged now.</p>
                  <p className="mt-1">
                    {pendingConfirm.sessionsCount > 1
                      ? 'These are booking requests. Ethan will confirm each session individually and email you a payment link for the ones he confirms. You can also pay from the Payments page.'
                      : "This is a booking request. Once Ethan confirms it, you'll get a payment link by email and the booking will appear on your Payments page."}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-6 border-t bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={() => setPendingConfirm(null)}
                disabled={submittingConfirm}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!pendingConfirm) return;
                  setSubmittingConfirm(true);
                  try {
                    await pendingConfirm.onConfirm();
                    setPendingConfirm(null);
                  } catch {
                    // Error already surfaced inside onConfirm via alert; keep modal open so user can retry/cancel.
                  } finally {
                    setSubmittingConfirm(false);
                  }
                }}
                disabled={submittingConfirm}
                className="btn btn-primary"
              >
                {submittingConfirm
                  ? 'Submitting…'
                  : `Confirm booking — ${formatPrice(pendingConfirm.totalPrice)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPage;